import { useId, type ReactNode } from "react";
import { Select, type ComboboxItem, type ComboboxLikeRenderOptionInput } from "@mantine/core";
import { useTranslation } from "react-i18next";

import type { SupportedHardware } from "@/contracts";
import { cn } from "@/lib/cn";
import { findCatalogueEntry } from "@/features/hardware/catalogue";
import { DEVICE_TYPE_LABELS } from "./device-labels";

interface HardwareSelectFieldProps {
  label: string;
  options: readonly SupportedHardware[];
  value: string;
  onChange: (value: string) => void;
  /** Empty-value placeholder when nothing is chosen yet. */
  placeholder: string;
  /**
   * When false, the empty sentinel is omitted (edit of recognised hardware).
   * Defaults to true for registration.
   */
  allowEmpty?: boolean;
  /**
   * Label for the empty option when `allowEmpty` is true. Falls back to
   * `placeholder` (e.g. "Keep as recorded" on unsupported edit).
   */
  emptyOptionLabel?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  onBlur?: () => void;
  className?: string;
}

/**
 * Hardware picker with product thumbnail and measurement type.
 *
 * Registration and edit need the operator to recognise the physical unit, not
 * only a model string — catalogue photos and `devices:type.*` labels exist for
 * that. Kept as a feature control rather than extending SelectField so plain
 * selects stay text-only.
 */
export function HardwareSelectField({
  label,
  options,
  value,
  onChange,
  placeholder,
  allowEmpty = true,
  emptyOptionLabel,
  hint,
  error,
  required,
  disabled,
  name,
  onBlur,
  className,
}: HardwareSelectFieldProps) {
  const { t } = useTranslation("devices");
  const generatedId = useId();
  const hintId = hint ? `${generatedId}-hint` : undefined;
  const errorId = error ? `${generatedId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  const data: ComboboxItem[] = [
    ...(allowEmpty ? [{ value: "", label: emptyOptionLabel ?? placeholder }] : []),
    ...options.map((hardware) => ({
      value: hardware.id,
      label: `${hardware.manufacturer} ${hardware.model}`,
    })),
  ];

  const selected = options.find((hardware) => hardware.id === value);
  const selectedImage = selected ? findCatalogueEntry(selected.id)?.imageSrc : undefined;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center gap-0.5">
        <label htmlFor={generatedId} className="text-sm font-medium text-ink">
          {label}
        </label>
        {required ? (
          <span className="text-danger" aria-hidden="true">
            *
          </span>
        ) : null}
      </div>

      <Select
        id={generatedId}
        data={data}
        value={value}
        onChange={(next) => onChange(next ?? "")}
        searchable={options.length > 5}
        nothingFoundMessage={t("hardwareNoMatches")}
        autoComplete="off"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className="w-full"
        styles={{ section: { overflow: "hidden" } }}
        allowDeselect={false}
        comboboxProps={{ withinPortal: false }}
        renderOption={(item) => renderHardwareOption(item, options)}
        leftSection={
          selectedImage ? (
            <HardwareThumb src={selectedImage} alt="" />
          ) : undefined
        }
        leftSectionWidth={selectedImage ? 36 : undefined}
        {...(name === undefined ? {} : { name })}
        {...(onBlur === undefined ? {} : { onBlur })}
        {...(disabled === undefined ? {} : { disabled })}
        {...(required === undefined ? {} : { required })}
      />

      {hint ? (
        <p id={hintId} className="text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function renderHardwareOption(
  { option, checked }: ComboboxLikeRenderOptionInput<ComboboxItem>,
  options: readonly SupportedHardware[],
): ReactNode {
  if (option.value === "") {
    return <span className="text-ink-muted">{option.label}</span>;
  }

  const hardware = options.find((entry) => entry.id === option.value);
  const imageSrc = findCatalogueEntry(option.value)?.imageSrc;
  const typeLabel = hardware ? DEVICE_TYPE_LABELS[hardware.type] : undefined;

  return (
    <div className="flex items-center gap-3 py-0.5">
      {imageSrc ? <HardwareThumb src={imageSrc} alt="" /> : <span className="size-7 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className={cn("truncate text-sm text-ink", checked && "font-medium")}>{option.label}</p>
        {typeLabel ? <p className="truncate text-xs text-ink-muted">{typeLabel}</p> : null}
      </div>
    </div>
  );
}

function HardwareThumb({ src, alt }: { src: string; alt: string }) {
  return (
    <img
      src={src}
      alt={alt}
      width={28}
      height={28}
      className="size-7 max-h-7 max-w-7 shrink-0 rounded object-contain"
    />
  );
}
